import React, { useEffect } from 'react';

import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Alert,
  Box,
  Button,
  Center,
  Container,
  Grid,
  Paper,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../contexts/AuthContext';
import { EMAIL_REGEX, PASSWORD_REGEX } from '../utils/regex';

interface RegisterFormValues {
  email: string;
  password: string;
  confirmPassword: string;
  accountName: string;
  firstName: string;
  lastName: string;
}

const Register: React.FC = () => {
  const { register, isLoading, error, clearError } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    clearError();
  }, [clearError]);

  const form = useForm({
    mode: 'uncontrolled',
    initialValues: {
      email: '',
      password: '',
      confirmPassword: '',
      accountName: '',
      firstName: '',
      lastName: ''
    },
    validate: {
      email: (value) => (EMAIL_REGEX.test(value) ? null : 'Invalid email format'),
      password: (value) => (PASSWORD_REGEX.test(value) ? null : 'Password must contain at least 8 characters with uppercase, lowercase, number, and special character'),
      confirmPassword: (value, values) => 
        value !== values.password ? 'Passwords do not match' : null,
      accountName: (value) => (value.length < 1 ? 'Account name is required' : null),
      firstName: (value) => (value.length < 1 ? 'First name is required' : null),
      lastName: (value) => (value.length < 1 ? 'Last name is required' : null)
    }
  });

  const handleSubmit = async (values: RegisterFormValues) => {
    await register(
      values.email,
      values.password,
      values.accountName,
      values.firstName,
      values.lastName
    );
  };

  return (
    <Box
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}
    >
      <Container size="sm" style={{ width: '100%' }}>
        <Center>
          <Paper
            p="xl"
            radius="md"
            shadow="xl"
            style={{
              width: '100%',
              maxWidth: 600,
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              marginTop: '20px',
              marginBottom: '20px'
            }}
          >
            <Stack gap="lg">
              <div style={{ textAlign: 'center' }}>
                <Title c="dark" mb="xs" order={2}>
                  Create Your Account
                </Title>
                <Text c="dimmed" size="sm">
                  If your company already has an account, please contact us to be added instead.
                </Text>
              </div>

              <form onSubmit={(e) => {
                e.preventDefault();
                const validation = form.validate();
                if (validation.hasErrors) return;
                void handleSubmit(form.values);
              }}>
                <Stack gap="md">
                  <TextInput
                    required
                    label="Email"
                    name="email"
                    placeholder="abc@example.com"
                    size="md"
                    type="email"
                    {...form.getInputProps('email')}
                  />

                  <TextInput
                    required
                    label="Account Name"
                    name="accountName"
                    placeholder="Your care agency name"
                    size="md"
                    {...form.getInputProps('accountName')}
                  />

                  <Grid>
                    <Grid.Col span={6}>
                      <TextInput
                        required
                        label="First Name"
                        name="firstName"
                        placeholder="Enter your first name"
                        size="md"
                        {...form.getInputProps('firstName')}
                      />
                    </Grid.Col>
                    <Grid.Col span={6}>
                      <TextInput
                        required
                        label="Last Name"
                        name="lastName"
                        placeholder="Enter your last name"
                        size="md"
                        {...form.getInputProps('lastName')}
                      />
                    </Grid.Col>
                  </Grid>

                  <PasswordInput
                    required
                    label="Password"
                    name="password"
                    placeholder="Enter your password"
                    size="md"
                    {...form.getInputProps('password')}
                  />

                  <PasswordInput
                    required
                    label="Confirm Password"
                    name="confirmPassword"
                    placeholder="Confirm your password"
                    size="md"
                    {...form.getInputProps('confirmPassword')}
                  />

                  {error && (
                    <Alert
                      color="red"
                      icon={<FontAwesomeIcon icon={faExclamationTriangle} />}
                      title="Error"
                      variant="light"
                    >
                      {error}
                    </Alert>
                  )}

                  <Button
                    fullWidth
                    disabled={isLoading}
                    loading={isLoading}
                    radius="md"
                    size="md"
                    style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      border: 'none'
                    }}
                    type="submit"
                  >
                    {isLoading ? 'Creating Account...' : 'Create Account'}
                  </Button>
                </Stack>
              </form>

              <Text c="dimmed" size="sm" ta="center">
                Already have an account?{' '}
                <Text
                  c="blue"
                  component="span"
                  style={{ cursor: 'pointer' }}
                  onClick={() => void navigate('/login')}
                >
                  Sign in
                </Text>
              </Text>
            </Stack>
          </Paper>
        </Center>
      </Container>
    </Box>
  );
};

export default Register;
